import {computed, Injectable, Signal, signal} from '@angular/core';
import {DtTableRef} from '../models/dt-table-ref';

/**
 * # DtTableRegistry
 *
 * Singleton service that acts as a **registry + event bus** for all
 * DataTable instances on the page.
 *
 * ## Responsibilities
 *
 * - Stores a `Map<tableId → DtTableRef>` of every mounted table.
 * - Tracks the **active table**: the last one the user interacted with.
 * - Exposes reactive signals so any consumer (toolbar, action bar, breadcrumb)
 *   can respond to selection / sort changes without knowing which component
 *   tree branch the table lives in.
 *
 * ## Architecture notes
 *
 * ```
 *  DataTableComponent (n instances)
 *       │  registers on init, unregisters on destroy
 *       │  calls setActive() on every user interaction
 *       ▼
 *  DtTableRegistry  ◄──── injected by ────  TableActionsComponent
 *       │                                   OtherToolbarComponent
 *       │                                   BreadcrumbComponent
 *       ▼                                   …
 *  Map<id, DtTableRef>  + activeId signal
 * ```
 *
 * The registry is **generic-erased** internally (`Map<string, DtTableRef<any>>`)
 * but the `get<T>()` method lets call-sites cast back to a typed ref.
 *
 * ## Multi-table pages
 *
 * Each table registers with its own `tableId` (auto-generated or provided via
 * the `[tableId]` input).  Action bars that must target a **specific** table
 * use `registry.get<User>('users-table')`.  Generic toolbars that always
 * operate on whichever table is currently focused use `registry.activeRef`.
 */
@Injectable({providedIn: 'root'})
export class DtTableRegistry {

  // ── Internal state ────────────────────────────────────────────────────────

  /**
   * Immutable Map reference: to trigger signal reactivity we replace the
   * whole Map object on every mutation (same pattern Angular uses internally).
   */
  private readonly _instances = signal<ReadonlyMap<string, DtTableRef<any>>>(new Map());

  /** ID of the currently active (last-interacted) table. */
  private readonly _activeId = signal<string | null>(null);

  // ── Public reactive API ───────────────────────────────────────────────────

  /**
   * Snapshot of all registered table instances, keyed by their `tableId`.
   * Reactive: consumers can use this in computed() or effects.
   */
  readonly instances: Signal<ReadonlyMap<string, DtTableRef<any>>> =
    this._instances.asReadonly();

  /**
   * The `tableId` of the currently active table, or `null` if none is active
   * (e.g. before any interaction has occurred).
   */
  readonly activeId: Signal<string | null> = this._activeId.asReadonly();

  /**
   * Reactive handle to the currently active table.
   * `null` when no table is registered or none has been interacted with yet.
   *
   * Use this in toolbars / action bars that always target the focused table:
   *
   * ```ts
   * protected readonly table = inject(DtTableRegistry).activeRef;
   *
   * // In template:
   * // {{ table()?.selectionCount() }} items selected
   * // (deleteSelected)="table()?.clearSelection()"
   * ```
   */
  readonly activeRef: Signal<DtTableRef<any> | null> = computed(() => {
    const id = this._activeId();
    return id ? (this._instances().get(id) ?? null) : null;
  });

  /**
   * Convenience signal: selection of the active table (empty array if none).
   */
  readonly activeSelection: Signal<any[]> = computed(
    () => this.activeRef()?.selection() ?? [],
  );

  /**
   * Convenience signal: display data of the active table (empty array if none).
   */
  readonly activeDisplayData: Signal<any[]> = computed(
    () => this.activeRef()?.displayData() ?? [],
  );

  /**
   * Convenience signal: selection count of the active table.
   */
  readonly activeSelectionCount: Signal<number> = computed(
    () => this.activeRef()?.selectionCount() ?? 0,
  );

  // ── Registration ──────────────────────────────────────────────────────────

  /**
   * Called by `DataTableComponent` during initialization.
   * If a table with the same ID already exists it is silently replaced
   * (covers HMR / component reuse scenarios).
   */
  register(ref: DtTableRef<any>): void {
    this._instances.update(current => {
      const next = new Map(current);
      next.set(ref.tableId, ref);
      return next;
    });

    // Auto-activate the first table that registers (good UX default)
    if (this._activeId() === null) {
      this._activeId.set(ref.tableId);
    }
  }

  /**
   * Called by `DataTableComponent` on destroy (via `DestroyRef`).
   * If the unregistered table was active, the registry picks the next
   * available table (or sets active to null).
   */
  unregister(tableId: string): void {
    this._instances.update(current => {
      const next = new Map(current);
      next.delete(tableId);
      return next;
    });

    if (this._activeId() === tableId) {
      const remaining = [...this._instances().keys()];
      this._activeId.set(remaining[0] ?? null);
    }
  }

  /**
   * Marks a table as the currently active one.
   * Called automatically by `DataTableComponent` on every user interaction
   * (row click, sort, checkbox toggle).
   *
   * Can also be called programmatically to pre-activate a specific table:
   * ```ts
   * registry.setActive('orders-table');
   * ```
   */
  setActive(tableId: string): void {
    if (this._instances().has(tableId)) {
      this._activeId.set(tableId);
    }
  }

  // ── Typed accessors ───────────────────────────────────────────────────────

  /**
   * Returns the `DtTableRef` for a specific table ID, cast to the given type.
   * Returns `null` if no table with that ID is registered.
   *
   * ```ts
   * const usersTable = registry.get<User>('users-table');
   * const selected: User[] = usersTable?.selection() ?? [];
   * ```
   */
  get<T>(tableId: string): DtTableRef<T> | null {
    return (this._instances().get(tableId) as DtTableRef<T>) ?? null;
  }

  /**
   * Returns `true` if any table is currently registered.
   * Useful for disabling toolbar buttons during initial page load.
   */
  hasActive(): boolean {
    return this._activeId() !== null;
  }
}
